import React from 'react';
import { Button, Offcanvas, OffcanvasBody } from 'reactstrap';

/* Stars and the rating score are always green, whatever the score. */
const RATING_COLOR = '#28a745';

const renderStars = (rating, size = '1rem') => {
  const value = Math.round(Number(rating) || 0);
  return [1, 2, 3, 4, 5].map((star) => (
    <i
      key={star}
      className={`mdi ${star <= value ? 'mdi-star' : 'mdi-star-outline'}`}
      style={{ color: RATING_COLOR, fontSize: size }}
    ></i>
  ));
};

/* The delivery feedback form. Q1 is the star rating; the rest come from
   feedback_data, keyed as posted by the form. */
const DELIVERY_QUESTIONS = [
  {
    question: 'How satisfied are you with the overall delivery experience?',
    type: 'rating',
  },
  {
    question:
      "How would you rate the delivery team's behavior and professionalism?",
    key: 'deliveryStaffBehavior',
  },
  { question: 'Did your order arrive in good condition?', key: 'orderCondition' },
  { question: 'Was your order delivered on time?', key: 'onTimeDelivery' },
  {
    question: 'Would you recommend our delivery service to others?',
    key: 'recommend',
  },
];

/* feedback_data is a JSON column - parsed by the driver, but tolerate a string. */
const parseFeedbackData = (value) => {
  if (!value) return {};
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) || {};
  } catch (e) {
    return {};
  }
};

const DeliveryReviewDetailsOffcanvas = ({ isOpen, toggle, review }) => {
  const answers = parseFeedbackData(review?.feedback_data);

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '480px', backgroundColor: '#ffffff' }}
    >
      <div className="p-4 border-bottom bg-white sticky-top shadow-sm">
        <div className="d-flex justify-content-between align-items-center mb-0">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-warning bg-opacity-10 p-2 rounded-3">
              <i className="mdi mdi-star text-warning fs-4"></i>
            </div>
            <div>
              <h5 className="fw-bold mb-0 text-dark">Delivery Feedback</h5>
              <p className="text-muted small mb-0">
                {review?.shipment_reference || review?.reference_no}
              </p>
            </div>
          </div>
          <Button
            type="button"
            className="btn-close"
            onClick={toggle}
            aria-label="Close"
            style={{ backgroundColor: 'transparent', border: 'none' }}
          ></Button>
        </div>
      </div>

      <OffcanvasBody>
        {review ? (
          <>
            {/* Overall rating */}
            <div className="text-center border rounded-3 p-3 mb-4 bg-light">
              <div className="mb-1">{renderStars(review?.rating, '1.6rem')}</div>
              <h4
                className="fw-bold mb-0"
                style={{ color: RATING_COLOR }}
              >
                {review?.rating} / 5
              </h4>
              {review?.created_at && (
                <p className="text-muted small mb-0 mt-1">
                  Submitted on {review.created_at}
                </p>
              )}
            </div>

            {/* Questions & answers */}
            {DELIVERY_QUESTIONS.map((q, idx) => {
              const answer = q.type === 'rating' ? review?.rating : answers?.[q.key];
              return (
                <div key={q.question} className="mb-3">
                  <p className="fw-semibold mb-1">
                    {idx + 1}. {q.question}
                  </p>
                  {q.type === 'rating' ? (
                    <div>{renderStars(answer)}</div>
                  ) : answer ? (
                    <span
                      className="badge border px-2 py-1"
                      style={{
                        backgroundColor: '#f1f3f5',
                        color: '#495057',
                        borderColor: '#ced4da',
                      }}
                    >
                      {answer}
                    </span>
                  ) : (
                    <span className="text-muted small">No answer</span>
                  )}
                </div>
              );
            })}

            {/* Customer comment */}
            <div className="mt-4">
              <p className="fw-semibold mb-1">Comments</p>
              {review?.review ? (
                <div className="border rounded-3 p-3 bg-light">
                  {review.review}
                </div>
              ) : (
                <span className="text-muted small">No comments</span>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i
              className="mdi mdi-star-outline text-muted"
              style={{ fontSize: '2.5rem' }}
            ></i>
            <h6 className="text-dark fw-bold mt-2">No Feedback Available</h6>
          </div>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default DeliveryReviewDetailsOffcanvas;
